import { Injectable } from '@angular/core';
import { AuthRestService } from 'src/app/services/rest/auth-rest.service';
import { LoginDto, TokenDto } from '@api/auth/interfaces';
import decode from 'jwt-decode';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

export interface IAuthService {
	login(data: LoginDto): Promise<TokenDto>;
	logout(): Promise<void>;
	isLoggedIn: boolean;
}

@Injectable({
	providedIn: 'root'
})
export class AuthService implements IAuthService {
	private readonly STORAGE_KEY = 'jwt_token';

	private _token: string;
	private _userHash: string;

	constructor(private authRestService: AuthRestService) {
		const tokenDto = this.getCurrentToken();
		if (tokenDto) {
			this._token = tokenDto.token;
		}
	}

	currentAccount$(): Observable<any> {
		return this.authRestService.currentAccount$()
	}

	async login(data: LoginDto): Promise<TokenDto> {
		const tokenDto = await this.authRestService.login(data);
		this.setCurrentToken(tokenDto);
		this.generateUserHash(data.username)
		return tokenDto;
	}

	login$(data: LoginDto): Observable<TokenDto> {
		return this.authRestService.login$(data).pipe(map(token => {
			this.generateUserHash(data.username)
			this.setCurrentToken(token);
			return token;
		}))
	}

	private generateUserHash(stringToHash: string): void {
		this._userHash = btoa(stringToHash)
	}

	async logout(): Promise<void> {
		localStorage.removeItem(this.STORAGE_KEY);
		this._token = undefined;
	}

	logout$(): Observable<void> {
		localStorage.removeItem(this.STORAGE_KEY);
		this._token = undefined;

		return of(null);
	}

	private getCurrentToken(): TokenDto {
		const json = localStorage.getItem(this.STORAGE_KEY);
		if (!json) {
			return undefined;
		}
		try {
			const token = JSON.parse(json);
			if (token?.token)
				return token as TokenDto;
			return undefined;
		} catch (error) {
			console.error(error);
			return undefined;
		}
	}

	private setCurrentToken(tokenDto: TokenDto) {
		localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tokenDto));
		this._token = tokenDto.token;
	}

	get token(): string {
		return this._token;
	}

	get isLoggedIn(): boolean {
		return !this.isTokenExpired(this.token);
	}

	get userHash(): string {
		return this._userHash
	}

	decodeToken(token: string): any {
		return decode(token);
	}

	getTokenExpirationDate(token = this.token) {
		const decoded = this.decodeToken(token);
		if (!decoded || !decoded.exp) {
			return null;
		}
		const date = new Date(0);
		date.setUTCSeconds(decoded.exp);
		return date;
	}

	isTokenExpired(token: string, offsetSeconds = 0) {
		if (!token || token === '') {
			return true;
		}
		const date = this.getTokenExpirationDate(token);
		if (date === null) {
			return false;
		}
		return !(date.valueOf() > new Date().valueOf() + offsetSeconds * 1000);
	}
}
